import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useBrand } from '@/config/brands';
import { useTheme } from '@/contexts/ThemeContext';
import { StateChoroplethMap } from './StateChoroplethMap';
import type {
  InternationalCategoryComparison,
  InternationalOverallComparison,
  InternationalCountry,
} from '@/hooks/useStateInternationalStats';
import type { StateScenarioOverallStat } from '@/hooks/useStateScenarioStats';

interface StateInternationalViewProps {
  categoryComparison: InternationalCategoryComparison[];
  overallComparison: InternationalOverallComparison[];
  countries: Map<string, InternationalCountry>;
  categories: string[];
  stateOverallStats: StateScenarioOverallStat[];
  selectedState: string | null;
  onStateSelect: (state: string | null) => void;
}

const STATE_NAMES: Record<string, string> = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', DC: 'District of Columbia',
  FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois',
  IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
  ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota',
  MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada',
  NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York',
  NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon',
  PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota',
  TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont', VA: 'Virginia',
  WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
};

/** Short label for category heatmap columns */
function shortCat(cat: string): string {
  const map: Record<string, string> = {
    'Cancer & Oncology': 'Cancer',
    'Cardiac & Cardiovascular': 'Cardiac',
    'Chronic Disease Management': 'Chronic',
    'Dental & Oral Surgery': 'Dental',
    'Diagnostic & Imaging': 'Imaging',
    'Emergency & Trauma': 'ER/Trauma',
    'Eye & Vision': 'Eye',
    'Gastrointestinal': 'GI',
    'Infectious Disease': 'Infect.',
    'Mental Health & Substance': 'Mental',
    'Neurological': 'Neuro',
    "Obstetric & Women's Health": 'OB/GYN',
    'Organ & Transplant': 'Transplant',
    'Orthopedic & Spine': 'Ortho',
    'Pediatric': 'Peds',
    'Rehabilitation': 'Rehab',
    'Respiratory & Pulmonary': 'Resp.',
    'Urological & Renal': 'Uro/Renal',
  };
  return map[cat] || cat.split(' ')[0].slice(0, 8);
}

/** Multiplier → heat color (green 1x → yellow 5x → orange 15x → red 50x+) */
function heatColor(mult: number): string {
  if (mult <= 2) return 'rgba(34,197,94,0.5)';
  if (mult <= 5) return 'rgba(234,179,8,0.45)';
  if (mult <= 15) return 'rgba(249,115,22,0.5)';
  if (mult <= 30) return 'rgba(239,68,68,0.55)';
  if (mult <= 50) return 'rgba(220,38,38,0.65)';
  return 'rgba(185,28,28,0.75)';
}

export function StateInternationalView({
  categoryComparison,
  overallComparison,
  countries,
  categories,
  stateOverallStats,
  selectedState,
  onStateSelect,
}: StateInternationalViewProps) {
  const { t } = useTranslation(['rankings', 'common']);
  const brand = useBrand();
  const { isDark } = useTheme();
  const isAhp = brand.key === 'ahp';

  const [selectedCountry, setSelectedCountry] = useState<string>('CA');

  // Theme
  const cardBg = isAhp
    ? (isDark ? 'rgba(10,22,40,0.85)' : 'rgba(255,255,255,0.95)')
    : 'rgba(0,0,0,0.85)';
  const borderColor = isAhp
    ? (isDark ? 'rgba(197,165,90,0.2)' : 'rgba(0,0,0,0.1)')
    : 'rgba(255,255,255,0.1)';
  const textColor = isAhp ? (isDark ? '#FAF9F6' : '#1B1B1B') : '#fff';
  const mutedText = isAhp ? (isDark ? 'rgba(250,249,246,0.6)' : 'rgba(0,0,0,0.5)') : 'rgba(255,255,255,0.5)';

  // State markup lookup (from scenario overall stats)
  const stateMarkupMap = useMemo(() => {
    const m = new Map<string, number>();
    for (const s of stateOverallStats) {
      m.set(s.state, Number(s.median_markup_pct));
    }
    return m;
  }, [stateOverallStats]);

  // Compute map data: state → estimated multiplier vs selected country
  const selectedCountryOverall = overallComparison.find(c => c.country_code === selectedCountry);
  const nationalMultiplier = selectedCountryOverall ? Number(selectedCountryOverall.us_vs_intl_multiplier) : 1;

  const mapData = useMemo(() => {
    const m: Record<string, number> = {};
    for (const [state, markup] of stateMarkupMap) {
      m[state] = nationalMultiplier * (1 + markup / 100);
    }
    return m;
  }, [stateMarkupMap, nationalMultiplier]);

  // Get top countries sorted by multiplier (exclude countries with very few scenarios)
  const topCountries = useMemo(() => {
    return overallComparison
      .filter(c => Number(c.scenario_count) >= 50)
      .sort((a, b) => Number(b.us_vs_intl_multiplier) - Number(a.us_vs_intl_multiplier));
  }, [overallComparison]);

  // Build heatmap data: rows = categories, columns = top countries
  const heatmapCountries = useMemo(() => {
    return topCountries.slice(0, 8);
  }, [topCountries]);

  const heatmapData = useMemo(() => {
    const lookup = new Map<string, number>();
    for (const row of categoryComparison) {
      lookup.set(`${row.category}::${row.country_code}`, Number(row.us_vs_intl_multiplier));
    }
    return { lookup };
  }, [categoryComparison]);

  // Total scenarios used
  const totalScenarios = selectedCountryOverall ? Number(selectedCountryOverall.scenario_count) : 0;

  // Selected state multiplier
  const selectedStateMarkup = selectedState ? stateMarkupMap.get(selectedState) : undefined;
  const selectedStateMultiplier = selectedStateMarkup != null
    ? nationalMultiplier * (1 + selectedStateMarkup / 100)
    : undefined;

  const countryInfo = countries.get(selectedCountry);

  return (
    <div className="space-y-6">
      {/* Headline banner */}
      <div
        className="rounded-xl p-5 text-center"
        style={{ backgroundColor: isAhp ? (isDark ? 'rgba(178,34,52,0.15)' : 'rgba(178,34,52,0.08)') : 'rgba(220,38,38,0.15)', border: `1px solid ${borderColor}` }}
      >
        <div className="text-2xl md:text-3xl font-bold mb-1" style={{ color: textColor }}>
          {t('stateComparison.international.headlineSimple', { multiplier: nationalMultiplier.toFixed(1), country: `${countryInfo?.flag_emoji || ''} ${countryInfo?.country_name || selectedCountry}` })}
        </div>
        <div className="text-sm" style={{ color: mutedText }}>
          {t('stateComparison.international.basedOnScenarios', { count: totalScenarios })}
        </div>
        {selectedState && selectedStateMultiplier && (
          <div className="mt-2 text-lg font-semibold" style={{ color: isAhp ? '#C5A55A' : '#00ff41' }}>
            {t('stateComparison.international.stateMultiplier', { state: STATE_NAMES[selectedState] || selectedState, multiplier: selectedStateMultiplier.toFixed(1), country: countryInfo?.country_name || selectedCountry })}
          </div>
        )}
      </div>

      {/* Country selector + Choropleth map */}
      <div className="rounded-xl p-4" style={{ backgroundColor: cardBg, border: `1px solid ${borderColor}` }}>
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <span className="text-sm font-medium" style={{ color: textColor }}>{t('stateComparison.international.compareUsTo')}</span>
          <select
            value={selectedCountry}
            onChange={(e) => setSelectedCountry(e.target.value)}
            className="text-sm px-3 py-1.5 rounded-lg outline-none"
            style={{
              backgroundColor: isAhp ? (isDark ? 'rgba(0,20,60,0.8)' : 'rgba(0,0,0,0.05)') : 'rgba(255,255,255,0.1)',
              color: textColor,
              border: `1px solid ${borderColor}`,
            }}
          >
            {topCountries.map((c) => {
              const info = countries.get(c.country_code);
              return (
                <option key={c.country_code} value={c.country_code}>
                  {info?.flag_emoji || ''} {info?.country_name || c.country_code} ({Number(c.us_vs_intl_multiplier).toFixed(1)}x)
                </option>
              );
            })}
          </select>
        </div>

        <StateChoroplethMap
          data={mapData}
          selectedState={selectedState}
          onStateClick={(s) => onStateSelect(s === selectedState ? null : s)}
          valueLabel={t('stateComparison.international.usAvgVsCountry', { country: countryInfo?.country_name || selectedCountry })}
          formatValue={(v) => `${v.toFixed(1)}x`}
        />
      </div>

      {/* Country comparison cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {topCountries.map((c) => {
          const info = countries.get(c.country_code);
          const mult = Number(c.us_vs_intl_multiplier);
          const isSelected = c.country_code === selectedCountry;
          const stateMult = selectedStateMarkup != null
            ? mult * (1 + selectedStateMarkup / 100)
            : null;

          return (
            <button
              key={c.country_code}
              onClick={() => setSelectedCountry(c.country_code)}
              className="rounded-xl p-3 text-left transition-all"
              style={{
                backgroundColor: cardBg,
                border: `2px solid ${isSelected ? (isAhp ? '#C5A55A' : '#00ff41') : borderColor}`,
                opacity: isSelected ? 1 : 0.8,
              }}
            >
              <div className="text-lg mb-0.5">{info?.flag_emoji || ''}</div>
              <div className="text-xs font-semibold truncate" style={{ color: textColor }}>
                {info?.country_name || c.country_code}
              </div>
              <div className="text-xl font-bold mt-1" style={{ color: '#ef4444' }}>
                {mult.toFixed(1)}x
              </div>
              <div className="text-[10px]" style={{ color: mutedText }}>
                {t('stateComparison.international.usAvgVsCountry', { country: c.country_code })}
              </div>
              {selectedState && stateMult && (
                <div className="text-xs font-semibold mt-1" style={{ color: isAhp ? '#C5A55A' : '#00ff41' }}>
                  {selectedState}: {stateMult.toFixed(1)}x
                </div>
              )}
              <div className="text-[10px] mt-1" style={{ color: mutedText }}>
                {info?.healthcare_system || ''}
              </div>
            </button>
          );
        })}
      </div>

      {/* Cost comparison: US avg vs selected country avg */}
      {selectedCountryOverall && (
        <div className="rounded-xl p-4" style={{ backgroundColor: cardBg, border: `1px solid ${borderColor}` }}>
          <h3 className="text-sm font-semibold mb-3" style={{ color: textColor }}>
            {t('stateComparison.international.avgScenarioCost', { flag: countryInfo?.flag_emoji || '', country: countryInfo?.country_name || selectedCountry })}
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-lg p-3" style={{ backgroundColor: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)' }}>
              <div className="text-xs" style={{ color: mutedText }}>{t('stateComparison.international.usNationalAverage')}</div>
              <div className="text-xl font-bold" style={{ color: '#ef4444' }}>
                ${Math.round(Number(selectedCountryOverall.avg_us_national_cost)).toLocaleString()}
              </div>
            </div>
            <div className="rounded-lg p-3" style={{ backgroundColor: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.2)' }}>
              <div className="text-xs" style={{ color: mutedText }}>{t('stateComparison.international.countryAverage', { country: countryInfo?.country_name || selectedCountry })}</div>
              <div className="text-xl font-bold" style={{ color: '#22c55e' }}>
                ${Math.round(Number(selectedCountryOverall.avg_intl_cost)).toLocaleString()}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Category x Country heatmap */}
      <div className="rounded-xl p-4" style={{ backgroundColor: cardBg, border: `1px solid ${borderColor}` }}>
        <h3 className="text-sm font-semibold mb-1" style={{ color: textColor }}>
          {t('stateComparison.international.heatmapTitle')}
        </h3>
        <div className="text-xs mb-3" style={{ color: mutedText }}>
          {t('stateComparison.international.heatmapDescription')}
        </div>

        {/* Legend */}
        <div className="flex items-center gap-3 mb-3 text-[10px]" style={{ color: mutedText }}>
          <span className="flex items-center gap-1">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: 'rgba(34,197,94,0.5)' }} />
            &le;2x
          </span>
          <span className="flex items-center gap-1">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: 'rgba(234,179,8,0.45)' }} />
            2-5x
          </span>
          <span className="flex items-center gap-1">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: 'rgba(249,115,22,0.5)' }} />
            5-15x
          </span>
          <span className="flex items-center gap-1">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: 'rgba(239,68,68,0.55)' }} />
            15-30x
          </span>
          <span className="flex items-center gap-1">
            <div className="w-3 h-3 rounded" style={{ backgroundColor: 'rgba(185,28,28,0.75)' }} />
            50x+
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-[10px]" style={{ color: textColor }}>
            <thead>
              <tr>
                <th className="text-left px-1.5 py-1.5 sticky left-0 z-10" style={{ backgroundColor: cardBg, minWidth: 70 }}>
                  {t('stateComparison.international.category')}
                </th>
                {heatmapCountries.map((c) => {
                  const info = countries.get(c.country_code);
                  return (
                    <th key={c.country_code} className="text-center px-1 py-1.5 whitespace-nowrap" style={{ minWidth: 50 }}>
                      <div>{info?.flag_emoji}</div>
                      <div className="font-medium">{c.country_code}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {categories.map((cat) => (
                <tr key={cat} className="border-t" style={{ borderColor }}>
                  <td className="px-1.5 py-1 font-medium sticky left-0 z-10 whitespace-nowrap" style={{ backgroundColor: cardBg }}>
                    {shortCat(cat)}
                  </td>
                  {heatmapCountries.map((c) => {
                    const mult = heatmapData.lookup.get(`${cat}::${c.country_code}`);
                    return (
                      <td
                        key={c.country_code}
                        className="text-center px-1 py-1 font-mono"
                        style={{
                          backgroundColor: mult ? heatColor(mult) : 'transparent',
                          color: mult && mult > 15 ? '#fff' : textColor,
                        }}
                        title={mult ? `US charges ${mult.toFixed(1)}x more than ${c.country_code} for ${cat}` : 'No data'}
                      >
                        {mult ? `${mult.toFixed(0)}x` : '—'}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Methodology footnote */}
      <div className="text-xs" style={{ color: mutedText }}>
        <p>
          {t('stateComparison.international.methodology')}{' '}
          <Link
            to="/data-methodology#international-comparison"
            className="underline hover:opacity-80"
            style={{ color: isAhp ? '#C5A55A' : '#00ff41' }}
          >
            {t('stateComparison.international.fullMethodology')}
          </Link>
        </p>
      </div>
    </div>
  );
}
